import { type ReactNode } from 'react';
import {
  contrastTextColor,
  findOtherOption,
  formatChoiceWithOther,
  matchValueExpr,
  readOtherText,
  type ColorRule,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { EXCLUDED_POST_FIELD_KEYS } from './postFieldsHelpers';

/**
 * 投稿詳細の「投稿者が入力した項目」を **読み取り専用**で表示する汎用セクション。
 *
 * 設計思想 (ユーザー指示):
 *   - admin の項目設定「詳細表示」(`FieldDef.visible`) にチェックの入った項目を、
 *     分析・一覧・地図の **全ビュー連動**で表示する。
 *   - 旧 `EcwDetailSection` (Happy/Healthy/scope/duration/場所 をキー名・ラベル直書きした
 *     ECW 固定バッジ) を撤去し、本コンポーネント 1 本に統一。表示は完全に postFields 定義
 *     (label / type / options / displayOrder) 駆動 — モード固有のハードコードを持たない。
 *   - 色は「マーカー色ルール (ColorRule, `District.mapDisplay.colorRule`)」の色設定にのみ基づく。
 *     value 形式 ColorRule の対象 field の値に bucket 色を付ける。それ以外の演出 (Happy/Healthy
 *     グラデ等) は issue #25 で field 設定駆動として別途復元検討。
 *
 * `PostExtraFieldsForm` (編集 UI) の型×表示ロジックの読み取り専用版に相当。
 * 値の取得元は `post.extraFields[field.key]`。本文 (title/comment/location/photo/detail) は
 * 別 UI で表示済みのため `EXCLUDED_POST_FIELD_KEYS` で除外する。
 */

export interface PostDetailFieldsProps {
  /** 地区 dataSettings.postFields (`resolvePostFields` 経由で取得済を渡す)。 */
  fields: FieldDef[];
  /** 投稿者入力値 (post.extraFields)。 */
  values: Record<string, unknown> | null | undefined;
  /** 地区の ColorRule (`useDistrictColorRule()`)。値の着色に使用。null なら無着色。 */
  colorRule: ColorRule | null;
}

/** rating-scale の 0 が「未選択」を意味する (excludeZero) 場合などを考慮した「値あり」判定。 */
function hasValue(field: FieldDef, value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return false;
    if (field.type === 'rating-scale' && value === 0 && field.optionsMeta?.excludeZero === true) {
      return false;
    }
    return true;
  }
  // checkbox 等の boolean は「true (チェック済)」のみ表示 (false=未チェックはノイズ)。
  if (typeof value === 'boolean') return value === true;
  return Boolean(value);
}

/**
 * option の value → label を解決 (見つからなければ value をそのまま)。
 * 「その他」には記入内容を添える (「その他（記入内容）」)。
 */
function optionLabel(field: FieldDef, value: string, all: Record<string, unknown>): string {
  const label = field.options.find((o) => o.value === value)?.label ?? value;
  return findOtherOption(field)?.value === value
    ? formatChoiceWithOther(label, readOtherText(all, field.key))
    : label;
}

/**
 * value 形式 ColorRule で、この field の `value` に一致する bucket 色を返す (無ければ null)。
 * ColorRule.fieldKey は `extraFields.<key>` / `<key>` どちらの記法も許容する。
 * quadrant 形式は単一 field に map できないため着色しない (post 単位の種別バッジはヘッダが担う)。
 */
function bucketColor(colorRule: ColorRule | null, fieldKey: string, value: unknown): string | null {
  if (!colorRule || colorRule.kind !== 'value') return null;
  const target = colorRule.fieldKey.startsWith('extraFields.')
    ? colorRule.fieldKey.slice('extraFields.'.length)
    : colorRule.fieldKey;
  if (target !== fieldKey) return null;
  for (const b of colorRule.buckets) {
    if (matchValueExpr(b.expr, value)) return b.color;
  }
  return null;
}

/** 動的色チップ (admin 設定の #RRGGBB は semantic class 化できないため inline style 例外、ColorBadge と同方針)。 */
function ValueChip({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: color, color: contrastTextColor(color) }}
    >
      {children}
    </span>
  );
}

/** 1 項目の値を型別に読み取り専用描画する。 */
function FieldValue({
  field,
  value,
  all,
  colorRule,
}: {
  field: FieldDef;
  value: unknown;
  /** 投稿者入力値の全体 (「その他」の記入内容 `{key}__other` を引くため)。 */
  all: Record<string, unknown>;
  colorRule: ColorRule | null;
}) {
  switch (field.type) {
    case 'select': {
      const v = String(value);
      const label = optionLabel(field, v, all);
      const color = bucketColor(colorRule, field.key, value);
      return color ? (
        <ValueChip color={color}>{label}</ValueChip>
      ) : (
        <span className="text-sm text-slate-700">{label}</span>
      );
    }
    case 'multiselect': {
      // 各選択値を個別に bucketColor (matchValueExpr) で着色する (「いずれか一致で色付け」)。
      // post 全体を評価する evaluateColorRule とは別経路だが、表示は値単位なので意図どおり。
      const arr = Array.isArray(value) ? (value as unknown[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {arr.map((raw, i) => {
            const v = String(raw);
            const label = optionLabel(field, v, all);
            const color = bucketColor(colorRule, field.key, raw);
            return color ? (
              <ValueChip key={i} color={color}>
                {label}
              </ValueChip>
            ) : (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-700"
              >
                {label}
              </span>
            );
          })}
        </div>
      );
    }
    case 'rating-scale': {
      const n = Number(value);
      const text = n > 0 ? `+${n}` : String(n);
      const color = bucketColor(colorRule, field.key, value);
      return color ? (
        <ValueChip color={color}>{text}</ValueChip>
      ) : (
        <span className="text-sm font-semibold text-slate-700">{text}</span>
      );
    }
    case 'number': {
      const text = String(value);
      const color = bucketColor(colorRule, field.key, value);
      return color ? (
        <ValueChip color={color}>{text}</ValueChip>
      ) : (
        <span className="text-sm text-slate-700">{text}</span>
      );
    }
    case 'checkbox':
      return <span className="text-sm text-slate-700">はい</span>;
    case 'date':
      return <span className="text-sm text-slate-700">{String(value)}</span>;
    case 'text':
    case 'textarea':
    default:
      return (
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
          {String(value)}
        </p>
      );
  }
}

export function PostDetailFields({ fields, values, colorRule }: PostDetailFieldsProps) {
  const ef = values ?? {};
  const renderable = fields
    .filter((f) => f.visible !== false)
    // 入力フォームに出さない (mode='hidden') field は「投稿者が入力した項目」ではないので除外。
    // AI/trigger が extraFields に書く派生値が hidden field と同 key だった場合の誤表示も防ぐ
    // (getRenderablePostFields と同方針)。
    .filter((f) => f.mode !== 'hidden')
    .filter((f) => !EXCLUDED_POST_FIELD_KEYS.has(f.key))
    .filter((f) => f.type !== 'photo' && f.type !== 'location')
    .filter((f) => hasValue(f, ef[f.key]))
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  if (renderable.length === 0) return null;

  return (
    <section
      className="mb-3 p-3 rounded-md border border-slate-200 bg-slate-50/60 space-y-2"
      data-testid="post-detail-fields"
    >
      {renderable.map((f) => (
        <div key={f.key}>
          <div className="text-[11px] text-slate-500 mb-0.5">{f.label}</div>
          <FieldValue field={f} value={ef[f.key]} all={ef} colorRule={colorRule} />
        </div>
      ))}
    </section>
  );
}
